// they are being used idk they aren't beinf detected by eslint
import React, { useContext, useEffect, useMemo, useState } from "react";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { OrgContext } from "contexts/OrgContext";
import { CloudCredContext } from "contexts/CloudCredContext";
import { Capability } from "models/Capability.model";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { IApplication, VerbBuildStatus } from "models/Workspace.model";
import EnvironmentCTAButtons from "./EnvironmentCTAButtons";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import {
  CloudIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  RectangleGroupIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { toast } from "utils/toast";
import Button from "components/UI-lib/Button";
import { FreeTierPrice } from "components/FreeTier";
import { useQuery } from "@tanstack/react-query";
import agent from "server";
import { CloudInfoService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/cloud_info_connect";
import { createConnectClient } from "server/connect";
import CPUIcon from "assets/img/svg/cpuicon.svg?react";
import MapPin from "assets/img/svg/mappin.svg?react";
import DatabaseIcon from "assets/img/svg/databaseicon.svg?react";
import {
  classNames,
  displayInstanceCost,
  fetchLegacyNotebookList,
  parseStatusMessage,
  regionToReadable,
  shouldShowAcademicGrantAccess,
  yamlRawDump,
} from "components/utils";
import { WorkspaceStatus } from "models/Workspace.model";
import { UserContext } from "contexts/UserContext";

import { InstanceAttribute } from "components/Environment/InstanceAttribute";
import { DetailBreadcrumb } from "components/NodeDetail/shared/DetailBreadcrumb";
import { Tooltip } from "@mui/material";
import {
  DEFAULT_VERB_CONFIG,
  WorkspaceCreateContext,
} from "contexts/WorkspaceCreateContext";

import { getCloudCredForEnvironment } from "utils/cloudCreds";

import TabNavigator from "./TabNavigator";
import FlatCard from "components/UI-lib/FlatCard";
import NotebookSettings from "./TabContent/Notebook";
import AccessSettings from "./TabContent/Access";
import LogsSettings from "./TabContent/Logs";
import HistorySettings from "./TabContent/History";
import {
  displayPlatformType,
  formatMemory,
  getInstancePriceRate,
  getStorageCostNumber,
  isHealthy,
  isStartupScriptFailed,
  isStartupScriptPending,
} from "../../Environment/utils/index";
import { SimplifiedContainerOverview } from "../../Environment/shared/ContainerOverview";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import InstanceStatusIndicator, {
  LifecycleScriptStatusIndicator,
} from "../../Environment/shared/InstanceStatusIndicator";
import ConstanterStatusIndicator from "../../Environment/shared/ContainerStatusIndicator";
import { Spinner } from "@kui-react/spinner";
import { Environment } from "../../Environment/utils/types";
import {
  doesEnvironmentHaveGpu,
  getEnvironmentCloudCredID,
  getEnvironmentBillingState,
  getEnvironmentStatus,
  getEnvironmentStatusLabel,
  getGpuCountFromEnvironment,
  getGpuManufacturerFromEnvironment,
  findApplicationByHostnamePrefix,
  getGpuMemoryFromEnvironment,
  getGpuNameFromEnvironment,
  getHealthChecksFromEnvironment,
  getIngressApplicationsFromEnvironment,
  getMemoryFromEnvironment,
  getVcpuFromEnvironment,
  getVerbBuildStatusFromTasks,
  isEnvironmentBuildSame,
  isLifeCycleScriptsSame,
  parseTimestamp,
  shouldShowInstanceDetailsDespiteFailure,
  transformWorkspaceToEnvironment,
} from "../../Environment/utils/environtmentUtils";
import EnvironmentActions from "./Actions";

import { LifecycleScriptStatus } from "../../Environment/shared/BuildTypes";
import { ViewScriptModal } from "components/ViewScriptModal";
import { BillingContext } from "contexts/BillingContext";
import { formatMemoryForDisplay } from "../../Environment/Create/utils/index";
import { isBrevCloud } from "utils/instanceTypes";
import {
  FREE_TIER_NOTE,
  isFreeTierInstance,
  isFreeTierStorage,
  useFreeTier,
} from "utils/freeTierUtils";
import { useEnvironmentNetworkAccess } from "hooks/environment/useEnvironmentNetworkAccess";
import { findSecureLinkByHostnamePrefix } from "utils/environment/networkAccessFallback";
import { getEnvironmentArchitectures } from "utils/architecture";
import ArchitectureBadge from "components/Environment/shared/ArchitectureBadge";

const cloudInfoClient = createConnectClient(CloudInfoService);

interface ParamTypes {
  workspaceId: string;
  page?: string;
}

interface LocationState {
  cuda?: string;
  python?: string;
}

const EnvironmentSettings: React.FC = () => {
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [loading, setLoading] = useState(false);
  const [, setShowButton] = useState(false);
  const { state } = useLocation<LocationState>();
  const wsContext = useContext(WorkspaceContext);
  const orgContext = useContext(OrgContext);
  const { cloudCreds } = useContext(CloudCredContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const userContext = useContext(UserContext);
  const oneClickDeployContext = useContext(OneClickDeployContext);
  const history = useHistory();
  const { workspaceId } = useParams<ParamTypes>();
  const [jupyterApp, setJupyterApp] = useState<IApplication>();
  const [k8sDashboardApp, setK8sDashboardApp] = useState<IApplication>();
  const createContext = useContext(WorkspaceCreateContext);
  const [lifecycleScriptLog, setLifecycleScriptLog] = useState<string | null>(
    null,
  );
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [lifecycleScript, setLifecycleScript] = useState<string | null>(null);
  const billingContext = useContext(BillingContext);
  // Only Skybridge envs load secure links — pass null otherwise so the CTAs
  // fall back to Cloudflare and skip wasted GetNetworkInfo polling.
  const isSkybridge = environment?.labels?.["tunnel-provider"] === "skybridge";
  const { httpPorts: secureLinkHTTPPorts, resolveCtaHostname } =
    useEnvironmentNetworkAccess(isSkybridge ? environment : null);
  const jupyterSecureLink = useMemo(
    () =>
      findSecureLinkByHostnamePrefix(secureLinkHTTPPorts, "jupyter", [
        environment?.workspaceId,
        environment?.environment_id,
      ]),
    [
      secureLinkHTTPPorts,
      environment?.workspaceId,
      environment?.environment_id,
    ],
  );
  const k8sDashboardSecureLink = useMemo(
    () =>
      findSecureLinkByHostnamePrefix(secureLinkHTTPPorts, "k8s-dashboard", [
        environment?.workspaceId,
        environment?.environment_id,
      ]),
    [
      secureLinkHTTPPorts,
      environment?.workspaceId,
      environment?.environment_id,
    ],
  );
  const jupyterHostname = jupyterSecureLink?.hostname ?? jupyterApp?.hostname;
  const k8sDashboardHostname = k8sDashboardSecureLink?.hostname;

  const hash = window?.location?.hash?.slice(1) ?? "";
  const validStates = ["Logs", "Notebook", "Access", "History"];
  const [currentState, setCurrentState] = useState(
    validStates.includes(hash) ? hash : "Access",
  );

  useEffect(() => {
    window.location.hash = currentState;
  }, [currentState]);

  const [notebookList, setNotebookList] = useState<any[]>([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const basePriceAmount =
    environment?.instance?.instance_type_info?.base_price?.amount;
  const { isEntitled } = useFreeTier();
  const isFreeTier = isEntitled && isFreeTierInstance(basePriceAmount);
  const settingsDiskGiB = Number(
    getStorageCostNumber(environment?.instance?.disk_size || "") || 0,
  );
  const settingsStoragePricePerGbHr = environment?.instance?.instance_type_info
    ?.supported_storage?.[0]?.price_per_gb_hr?.amount
    ? Number(
        environment.instance.instance_type_info.supported_storage[0]
          .price_per_gb_hr.amount,
      )
    : undefined;
  const environmentBillingState = getEnvironmentBillingState(environment);
  const storageChargeIsFree =
    !environmentBillingState.storage ||
    !settingsStoragePricePerGbHr ||
    (isEntitled &&
      isFreeTierStorage(settingsDiskGiB, settingsStoragePricePerGbHr));
  const hasActiveUsage =
    environmentBillingState.compute || environmentBillingState.storage;
  const showGrantAccess = shouldShowAcademicGrantAccess(
    orgContext.activeOrg,
    instanceTypesContext.currencyDisplay,
  );
  const instancePriceRate = environment?.instance?.instance_type_info
    ?.supported_storage
    ? getInstancePriceRate(
        Number(
          environment.instance?.instance_type_info?.base_price?.amount || 0,
        ),
        Number(
          getStorageCostNumber(environment.instance?.disk_size || "") || 0,
        ),
        Number(
          environment.instance?.instance_type_info?.supported_storage?.[0]
            ?.price_per_gb_hr?.amount || 0,
        ),
        environmentBillingState.compute,
        environmentBillingState.storage,
      )
    : "";
  const allActiveChargesFree =
    isFreeTier &&
    hasActiveUsage &&
    storageChargeIsFree &&
    Number(instancePriceRate) > 0;

  useEffect(() => {
    const loadEnvironment = async () => {
      setLoading(true);
      const env = await wsContext.getWorkspace(workspaceId);
      if (env) {
        setEnvironment(env);
      } else {
        toast.error(
          "Could not find instance. Return to the console and try again.",
        );
      }
      setLoading(false);
    };
    loadEnvironment();
  }, [workspaceId]);

  useEffect(() => {
    const fetchNotebooks = async () => {
      try {
        const notebookList = await fetchLegacyNotebookList();
        setNotebookList(notebookList);
      } catch (e) {
        console.log(e);
      }
    };
    fetchNotebooks();
  }, []);

  useEffect(() => {
    if (!environment) {
      setJupyterApp(undefined);
      setK8sDashboardApp(undefined);
      return;
    }

    setJupyterApp(findApplicationByHostnamePrefix(environment, "jupyter"));
    setK8sDashboardApp(
      findApplicationByHostnamePrefix(environment, "k8s-dashboard"),
    );
  }, [environment]);

  const deleteEnvironment = async (workspaceId: string) => {
    setLoadingDelete(true);
    const res = await wsContext.deleteWorkspace(workspaceId);
    setLoadingDelete(false);
    if (res.success) {
      if (oneClickDeployContext?.environment?.workspaceId === workspaceId) {
        oneClickDeployContext.reset();
      }
      toast.success("Deleting Instance", {
        description:
          "It may take a few moments to see updates to your instance state",
      });
      // if currently on workspace page, redirect to environments list
      if (history.location.pathname.includes("/environments/")) {
        history.push(`/org/${orgContext.activeOrgId}/environments`);
      }
    } else {
      toast.error("Failed to delete instance", {
        description: res.message,
      });
    }
  };

  const { refetch: refetchEnvironment } = useQuery({
    queryKey: ["environment", workspaceId],
    queryFn: async () => {
      const res = await agent.Workspaces.get(workspaceId);
      if (res.success && res.data) {
        return transformWorkspaceToEnvironment(res.data);
      }
      return null;
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
    enabled: !!environment,
    staleTime: 1000,
    onSuccess: (data) => {
      if (!data || !environment) return;

      if (
        data.ssh_access?.length !== environment.ssh_access?.length ||
        data.instance?.firewall_rules?.ingress_rules?.length !==
          environment.instance?.firewall_rules?.ingress_rules?.length ||
        data.tasks?.length !== environment.tasks?.length ||
        data.instance?.status?.object_status !==
          environment.instance?.status?.object_status ||
        data.instance?.status?.lifecycle_status !==
          environment.instance?.status?.lifecycle_status ||
        data.status !== environment.status ||
        !isEnvironmentBuildSame(data.build, environment.build) ||
        data.lifecycle_scripts?.length !==
          environment.lifecycle_scripts?.length ||
        !isLifeCycleScriptsSame(
          data.lifecycle_scripts,
          environment.lifecycle_scripts,
        ) ||
        !isCheckHealthSame(
          getHealthChecksFromEnvironment(data),
          getHealthChecksFromEnvironment(environment),
        ) ||
        getVerbBuildStatusFromTasks(data.tasks || []) !==
          getVerbBuildStatusFromTasks(environment.tasks || [])
      ) {
        setEnvironment(data);
        wsContext.upsertEnvironment(data);
      }
    },
  });

  const lifecycleScriptId = environment?.lifecycle_scripts?.[0]?.id;

  useEffect(() => {
    if (!lifecycleScriptId) return;

    const loadLifecycleScript = async () => {
      const res = await agent.Workspaces.getWorkspaceLifeCycleScript(
        workspaceId,
        lifecycleScriptId,
      );
      if (res.data) {
        setLifecycleScript(res.data.attrs?.script || "");
      }
    };

    void loadLifecycleScript();
  }, [workspaceId, lifecycleScriptId]);

  const readLifecycleScriptLog = async (
    workspaceID: string,
    filePath: string,
  ) => {
    const res = await agent.Workspaces.readFileFromWorkspace(workspaceID, {
      filePath: filePath,
    });
    if (res.success && res.data && res.data.content !== "\n") {
      return res.data.content;
    } else {
      return null;
    }
  };

  const lifecycleScriptRun = environment?.lifecycle_scripts?.[0]?.last_run;
  const lifecycleScriptLogIsTerminal =
    lifecycleScriptRun?.status === LifecycleScriptStatus.COMPLETED ||
    lifecycleScriptRun?.status === LifecycleScriptStatus.FAILED;

  useQuery({
    // Separate runs and fetch final content once when the run becomes terminal.
    queryKey: [
      "lifecycleScriptLog",
      workspaceId,
      lifecycleScriptRun?.run_id,
      lifecycleScriptRun?.log_path,
      lifecycleScriptLogIsTerminal,
    ],
    queryFn: () =>
      readLifecycleScriptLog(
        workspaceId,
        environment?.lifecycle_scripts?.[0]?.last_run?.log_path || "",
      ),
    refetchInterval: lifecycleScriptLogIsTerminal ? false : 3000,
    staleTime: lifecycleScriptLogIsTerminal ? Infinity : 1000,
    refetchIntervalInBackground: false,
    enabled: !!(
      environment &&
      environment.lifecycle_scripts &&
      environment.lifecycle_scripts.length > 0 &&
      environment.lifecycle_scripts[0].last_run?.log_path
    ),
    onSuccess: (res) => {
      if (res) {
        setLifecycleScriptLog(res);
      }
    },
  });

  const healthStatus = useMemo(
    () =>
      environment
        ? isHealthy(environment, jupyterApp) &&
          !isStartupScriptPending(environment)
        : false,
    [environment, isHealthy, jupyterApp],
  );

  const k8sDashboardHealthStatus = useMemo(
    () =>
      environment
        ? Boolean(k8sDashboardHostname) &&
          isHealthy(environment, k8sDashboardApp) &&
          !isStartupScriptPending(environment)
        : false,
    [environment, isHealthy, k8sDashboardApp, k8sDashboardHostname],
  );

  const ctaButtons = useMemo(() => {
    if (!environment) return [];
    return getIngressApplicationsFromEnvironment(environment)
      .filter((app) => app.labels?.["cta-enabled"] === "true")
      .slice(0, 2)
      .map((app) => {
        const hostname = resolveCtaHostname(app);
        return {
          app,
          hostname,
          label: app.labels?.["cta-value"] || app.name,
          isHealthy:
            Boolean(hostname) &&
            isHealthy(environment, app) &&
            !isStartupScriptPending(environment),
        };
      });
  }, [environment, resolveCtaHostname, isHealthy]);

  const startupScriptFailed = isStartupScriptFailed(environment);

  const isCheckHealthSame = (a: any[], b: any[]) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;
    if (a.length > 0 && b.length > 0) {
      return a[0].status === b[0].status;
    }
    return true;
  };

  useEffect(() => {
    if (environment?.name) {
      document.title = `${environment.name} | Brev.dev`;
    } else {
      document.title = `Instance Settings | Brev.dev`;
    }
  }, [loading, environment]);

  useEffect(() => {
    // Add CUDA and Python loaded from template via query params
    let tempConfig = DEFAULT_VERB_CONFIG;
    if (state && state.cuda) {
      tempConfig.build.cuda = state.cuda;
    }
    if (state && state.python) {
      tempConfig.build.python_version = state.python;
    }
    createContext.setVerbYaml(yamlRawDump(tempConfig));
  }, []);

  const cloudCred = useMemo(() => {
    if (environment && cloudCreds.length > 0) {
      return getCloudCredForEnvironment(
        getEnvironmentCloudCredID(environment),
        cloudCreds,
      );
    }
    return null;
  }, [cloudCreds, environment]);

  const { data: capabilities = [], isLoading: loadingCapabilities } = useQuery({
    queryKey: ["capabilities", cloudCred?.cloudCredId],
    queryFn: async () => {
      if (!cloudCred?.cloudCredId) return [];

      const res = await cloudInfoClient.getCapabilities({
        cloudCredId: cloudCred.cloudCredId,
      });
      return res.capabilities as Capability[];
    },
    enabled: !!cloudCred?.cloudCredId,
  });

  const openNotebook = async (environment: Environment) => {
    if (!jupyterHostname) return;

    if (environment.build?.files && environment.build.files.length > 0) {
      let file = environment.build.files[0];
      if (
        file &&
        environment.build.verb &&
        !environment.build?.verb?.config?.includes("workingDirectory")
      ) {
        window.open(
          "https://" +
            jupyterHostname +
            "/lab/tree" +
            (file.file_directory?.startsWith("/") ? "" : "/") +
            file.file_directory,
          "_blank",
        );
      } else {
        window.open("https://" + jupyterHostname, "_blank");
      }
    } else {
      window.open("https://" + jupyterHostname, "_blank");
    }
  };

  const openDashboard = async () => {
    if (!k8sDashboardHostname) return;
    window.open("https://" + k8sDashboardHostname, "_blank");
  };

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  const isBrevCloudInstance = isBrevCloud(environment?.instance_type?.type);

  return (
    <>
      <div className="min-h-full">
        {/* Page heading */}
        <header className="pt-8 pb-3 sm:px-6 lg:px-8">
          <ViewScriptModal
            lifecycleScript={lifecycleScript || ""}
            show={showScriptModal}
            setShow={setShowScriptModal}
          />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
            <div className="flex-1 min-w-0 pb-3">
              <DetailBreadcrumb
                items={[
                  {
                    label: orgContext.activeOrg?.name,
                    onNavigate: () => {
                      history.push(
                        orgContext.activeOrgId
                          ? `/org/${orgContext.activeOrgId}/environments`
                          : "/",
                      );
                    },
                  },
                  { label: environment?.name },
                ]}
              />
              <div className="flex flex-row justify-between items-end">
                <div className="flex fle-row items-end">
                  <h1 className="mt-8 text-2xl font-bold leading-7 text-gray-900 dark:text-gray-100 sm:text-2xl sm:tracking-tight sm:truncate">
                    {environment?.name}
                  </h1>
                  {environment && (
                    <div className="ml-3 mt-2 flex items-center text-sm text-secondary mb-[2px]">
                      Created
                      {` ${parseTimestamp(
                        environment?.create_time,
                      )?.toLocaleString()}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3"></div>
              </div>
              {environment && (
                <>
                  <div className="flex flex-col sm:flex-row justify-between text-secondary">
                    <div className="flex flex-col sm:flex-row">
                      {environment.instance_type && (
                        <div className="mt-2 flex items-center text-sm">
                          <CPUIcon className="h-5 w-5 mr-1" />
                          <InstanceAttribute
                            instanceType={
                              environment.instance?.instance_type ||
                              environment.instance_type.type
                            }
                            isGPUInstance={doesEnvironmentHaveGpu(environment)}
                            gpuName={getGpuNameFromEnvironment(environment)}
                            gpuManufacturer={getGpuManufacturerFromEnvironment(
                              environment,
                            )}
                            vcpu={getVcpuFromEnvironment(environment)}
                            memory={formatMemory(
                              getMemoryFromEnvironment(environment) || "",
                              cloudCred?.providerId || "",
                            )}
                            gpuMemory={getGpuMemoryFromEnvironment(environment)}
                            gpuCount={getGpuCountFromEnvironment(environment)}
                          ></InstanceAttribute>
                        </div>
                      )}
                      <ArchitectureBadge
                        architectures={getEnvironmentArchitectures(environment)}
                        variant="metadata"
                        className="mt-2 mr-3"
                      />
                      {environment.instance?.disk_size && (
                        <div className="mt-2 flex items-center text-sm mr-3">
                          <DatabaseIcon
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-secondary"
                            aria-hidden="true"
                          />
                          {formatMemoryForDisplay(
                            environment.instance?.disk_size,
                          )}
                        </div>
                      )}
                      {environment.instance?.public_ip && (
                        <div className="mt-2 flex items-center text-sm mr-3">
                          <GlobeAltIcon
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-secondary"
                            aria-hidden="true"
                          />
                          {environment.instance?.public_ip}
                        </div>
                      )}
                      {environment.instance?.location && cloudCred && (
                        <div className="mt-2 flex items-center text-sm text-secondary mr-3">
                          <MapPin
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                          {regionToReadable[environment.instance?.location]
                            ? `${regionToReadable[environment.instance?.location].city}, ${regionToReadable[environment.instance?.location].country}`
                            : environment.instance?.location}
                          <span className="mr-1.5 ml-1.5">|</span>
                          <CloudIcon className="w-5 h-5 mr-2" />
                          <span>
                            {displayPlatformType(
                              isBrevCloudInstance
                                ? "Registered compute"
                                : environment.instance.instance_type_info
                                    ?.cloud ||
                                    environment.instance.instance_type_info
                                      ?.provider ||
                                    "",
                            )}
                          </span>
                          {!showGrantAccess && (
                            <>
                              <span className="mr-1.5 ml-1.5">|</span>
                              <Tooltip
                                title={
                                  <div>
                                    {allActiveChargesFree
                                      ? FREE_TIER_NOTE
                                      : environmentBillingState.compute
                                        ? environmentBillingState.storage
                                          ? "Compute and storage are currently charging."
                                          : "Compute is currently charging. Storage is not charging."
                                        : environmentBillingState.storage
                                          ? "Compute is not charging. Storage is currently charging."
                                          : "No active charges."}
                                    {instanceTypesContext.currencyDisplay ===
                                    "hours" ? (
                                      <div>
                                        Remaining GPU hour estimate is based on
                                        current instance state and remaining
                                        account balance.
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                  </div>
                                }
                                placement="top"
                              >
                                <div className="flex cursor-pointer">
                                  {environment && (
                                    <div className="flex items-center text-sm text-secondary">
                                      {environmentBillingState.compute ? (
                                        <PlayCircleIcon className="h-5 w-5 mr-1" />
                                      ) : (
                                        <PauseCircleIcon className="h-5 w-5 mr-1" />
                                      )}
                                      <div
                                        className={
                                          instanceTypesContext.currencyDisplay ===
                                          "hours"
                                            ? "text-highlight underline flex flex-row items-center gap-1"
                                            : ""
                                        }
                                      >
                                        {allActiveChargesFree ? (
                                          <FreeTierPrice
                                            price={instancePriceRate}
                                          />
                                        ) : (
                                          displayInstanceCost(
                                            instancePriceRate,
                                            billingContext.currentBalance,
                                            instanceTypesContext.currencyDisplay,
                                          )
                                        )}
                                        {instanceTypesContext.currencyDisplay ===
                                          "hours" && (
                                          <QuestionMarkCircleIcon className="h-5 w-5" />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      )}
                      {environment && (
                        <div
                          className="mt-2 flex items-center text-sm text-secondary mr-4"
                          id="instance-status"
                        >
                          <InstanceStatusIndicator
                            workspaceStatus={getEnvironmentStatus(environment)}
                            statusLabel={getEnvironmentStatusLabel(environment)}
                          />
                        </div>
                      )}
                    </div>
                    {environment && (
                      <EnvironmentActions
                        environment={environment}
                        capabilities={capabilities}
                        loadingCapabilities={loadingCapabilities}
                        deleteEnvironment={deleteEnvironment}
                        loadingDelete={loadingDelete}
                      />
                    )}
                  </div>
                  {environment && (
                    <div className="flex flex-row items-center mt-3 justify-between">
                      <div className="flex flex-row items-center">
                        <div className="flex flex-row items-center">
                          <RectangleGroupIcon className="h-6 w-6 text-gray-400 text-secondary mr-1.5" />
                          <SimplifiedContainerOverview
                            environmentBuild={environment.build}
                          />
                          {environment && (
                            <div
                              className="flex flex-row items-center justify-center ml-4"
                              id="container-status"
                            >
                              <ConstanterStatusIndicator
                                instanceStatus={getEnvironmentStatus(
                                  environment,
                                )}
                                containerStatus={
                                  getVerbBuildStatusFromTasks(
                                    environment.tasks || [],
                                  ) || VerbBuildStatus.Unset
                                }
                              />
                            </div>
                          )}
                        </div>
                        {environment.lifecycle_scripts &&
                          environment.lifecycle_scripts.length > 0 && (
                            <div className="flex flex-row items-center ml-4">
                              <DocumentTextIcon className="h-6 w-6 text-gray-400 text-secondary mr-1.5" />
                              <span
                                className="mr-2 text-sm text-secondary underline cursor-pointer hover:text-highlight"
                                onClick={() => {
                                  setShowScriptModal(true);
                                }}
                              >
                                script
                              </span>

                              <span>
                                <LifecycleScriptStatusIndicator
                                  lifecycleScriptStatus={
                                    environment.lifecycle_scripts[0].last_run
                                      ?.status || LifecycleScriptStatus.PENDING
                                  }
                                />
                              </span>
                            </div>
                          )}
                        {environment.labels?.launchableId && (
                          <div className="flex flex-row items-center ml-4">
                            <a
                              href={`/launchable/deploy?launchableID=${environment.labels.launchableId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex flex-row items-center text-sm text-secondary underline hover:text-highlight"
                            >
                              <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-1.5" />
                              View Launchable
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-row items-center gap-3">
                        <EnvironmentCTAButtons
                          ctaButtons={ctaButtons.map(
                            ({ hostname, label, isHealthy }) => ({
                              hostname,
                              label,
                              isHealthy,
                            }),
                          )}
                          jupyter={
                            jupyterHostname || jupyterApp
                              ? {
                                  hostname: jupyterHostname,
                                  isHealthy: healthStatus,
                                }
                              : undefined
                          }
                          startupScriptFailed={startupScriptFailed}
                          onOpenNotebook={() => {
                            if (environment) {
                              openNotebook(environment);
                            }
                          }}
                        />
                        {environment &&
                          k8sDashboardApp &&
                          environment.build?.vm?.k8s?.isDashboardEnabled && (
                            <div>
                              <Tooltip
                                title={
                                  !k8sDashboardHealthStatus
                                    ? "Dashboard not yet available (initialization may take several minutes after instance starts)"
                                    : startupScriptFailed
                                      ? "Setup script failed — the dashboard may not work correctly"
                                      : ""
                                }
                                placement="top"
                              >
                                <span>
                                  <Button
                                    className={classNames(
                                      k8sDashboardHealthStatus
                                        ? "pulse-ready-button"
                                        : "",
                                    )}
                                    label="Open Dashboard"
                                    type={
                                      k8sDashboardHealthStatus
                                        ? "primary"
                                        : "secondary"
                                    }
                                    loading={false}
                                    onClick={() => {
                                      openDashboard();
                                    }}
                                    disabled={!k8sDashboardHealthStatus}
                                    iconLeft={
                                      <RectangleGroupIcon
                                        className="-ml-1 mr-2 h-5 w-5 text-white"
                                        aria-hidden="true"
                                      />
                                    }
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </header>
        {environment &&
        getEnvironmentStatus(environment) === WorkspaceStatus.Failure &&
        !shouldShowInstanceDetailsDespiteFailure(environment) ? (
          <>
            {" "}
            <main className="pt-8 pb-16 sm:px-6 lg:px-8">
              {/* Ensures only user can access his own */}
              <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
                {environment?.labels?.userId === userContext.me?.id ? (
                  <>
                    <div className="bg-white dark:bg-zinc-900">
                      <div className="flex flex-col">
                        <span className="text-red-500 font-bold">
                          There was an error provisioning your instance:
                        </span>
                        <div>
                          <span className="text-red-500">
                            "
                            {parseStatusMessage(
                              environment.instance?.provision_status?.message ||
                                "",
                            ).trim()}
                            "
                          </span>
                        </div>
                        <span className="text-red-500 font-bold">
                          Please retry creating a new instance on a different
                          cloud!
                        </span>
                        <div className="flex items-center space-x-3 mt-5">
                          <Button
                            label="Delete Workspace"
                            onClick={() =>
                              deleteEnvironment(environment?.workspaceId || "")
                            }
                            type="danger"
                            loading={loadingDelete}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </main>
          </>
        ) : (
          <>
            <main className="pb-16 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div className="dark:bg-zinc-900 mb-8">
                  <div className="bg-gray-50 dark:bg-zinc-900">
                    <TabNavigator
                      environment={environment}
                      setCurrentState={setCurrentState}
                      currentState={currentState}
                    />
                  </div>
                </div>
                {currentState === "None" && (
                  <div className="flex justify-center w-full">
                    <FlatCard className="mt-3" isLoading={false}>
                      <p className="text-sm text-gray-700 dark:text-secondary font-mono">
                        You do not have access to this instance.
                      </p>
                    </FlatCard>
                  </div>
                )}
                {currentState === "Logs" && environment && (
                  <>
                    <LogsSettings
                      workspaceId={environment.workspaceId || ""}
                      setShowButton={setShowButton}
                      environment={environment}
                      initialVerbLoad={false}
                      lifecycleScriptLog={lifecycleScriptLog}
                    />
                  </>
                )}
                {currentState === "Notebook" && environment && (
                  <>
                    <NotebookSettings
                      notebookList={notebookList}
                      environment={environment}
                    />
                  </>
                )}

                {currentState === "Access" && environment && (
                  <>
                    <AccessSettings
                      environment={environment}
                      workspaceCapabilities={capabilities}
                      refetchEnvironment={refetchEnvironment}
                    />
                  </>
                )}
                {currentState === "History" && environment && (
                  <>
                    <HistorySettings environment={environment} />
                  </>
                )}
              </div>
            </main>
          </>
        )}
      </div>
    </>
  );
};

export default EnvironmentSettings;
